import { z } from 'zod';
import { amountError, arrivalErrorOf, endDateErrorOf, endTimeErrorOf } from './order-form-modal/utils';
import { CONTACT_PHONE_RE } from '../constants';

/** 返回当天的 YYYY-MM-DD（本地时区） */
function todayStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * 订单表单验证 Schema 工厂
 * @param allowPastDates 当前是否开启补录模式。true=允许选过去日期，false=仅允许今天及未来
 * @param amountFromItems 有有效明细时为 true：订单金额由明细合计接管，手填框的值不再上送，也就不能拿它挡提交
 * @param backfillLockedByAdmin 管理员已全局熔断补录：此时提示让用户去找管理员，而不是自己去开开关
 * @param startDateLocked 编辑态：预约日期只读回显，**不参与**补录判据。
 *   历史单的日期本就落在过去，而这一格已经不给改了——再按补录挡一次，用户面对的是一个
 *   既改不动又过不去的死表单，连补一句备注都保存不了。服务端同一口径（PUT 不查补录窗口）。
 */
export function createOrderFormSchema(
  allowPastDates: boolean,
  amountFromItems = false,
  backfillLockedByAdmin = false,
  startDateLocked = false
) {
  return z.object({
    orderCategory: z.string().min(1, '请选择订单分类'),
    serviceTypeId: z.string().min(1, '请选择服务类型'),
    customerId: z.string().min(1, '请选择关联客户'),
    contactPerson: z.string().optional(),
    contactPhone: z.string().optional().refine(
      v => !v?.trim() || CONTACT_PHONE_RE.test(v.trim()),
      '对接电话格式不正确（须为 11 位手机号）'
    ),
    activityName: z.string().optional(),
    activityAddress: z.string().optional(),
    orderStartDate: z
      .string()
      .min(1, '请填写预约日期')
      .refine(
        v => startDateLocked || allowPastDates || v >= todayStr(),
        {
          message: backfillLockedByAdmin
            ? '仅允许选择今天及以后的日期；平台补录已由管理员统一关闭，如需补录历史订单请联系管理员'
            : '仅允许选择今天及以后的日期，如需补录历史订单请先在《系统设置 · 订单》中开启补录模式',
        }
      ),
    orderEndDate: z.string().optional(),
    isFullDay: z.boolean(),
    orderTimeStart: z.string().optional(),
    orderTimeEnd: z.string().optional(),
    arrivalDateTime: z.string().optional(),
    orderAmount: z.string().optional(),
    paidAmount: z.string().optional(),
    paymentMethod: z.string().optional(),
    paymentDate: z.string().optional(),
    orderSpecialInstructions: z.string().optional(),
  }).superRefine((v, ctx) => {
    // 三条跨字段闸门必须落在 schema 里，不能只靠弹窗的 setError：
    // react-hook-form 提交时由 resolver 重算并整体替换 errors，手动设的错误在那一刻全部丢失，
    // 于是「只标红不拦提交」——非法档期照样发给后端，换回一个 400 让用户对着红字猜。
    const end = endDateErrorOf(v.orderStartDate, v.orderEndDate ?? '');
    if (end) ctx.addIssue({ code: 'custom', path: ['orderEndDate'], message: end });
    const endTime = endTimeErrorOf(
      v.orderStartDate, v.orderEndDate ?? '', v.isFullDay, v.orderTimeStart ?? '', v.orderTimeEnd ?? ''
    );
    if (endTime) ctx.addIssue({ code: 'custom', path: ['orderTimeEnd'], message: endTime });
    const arrival = arrivalErrorOf(
      v.arrivalDateTime ?? '', v.orderStartDate, v.isFullDay, v.orderTimeStart ?? ''
    );
    if (arrival) ctx.addIssue({ code: 'custom', path: ['arrivalDateTime'], message: arrival });
    // 两条金额闸门：1.999 这类三位小数以前能一路发到后端，被 Decimal(10,2) 悄悄四舍五入成 2.00，
    // 账对不上时查不到原因；上限则是列宽，越过就是 Postgres numeric overflow（500）。
    if (!amountFromItems) {
      const amount = amountError('订单金额', v.orderAmount ?? '');
      if (amount) ctx.addIssue({ code: 'custom', path: ['orderAmount'], message: amount });
    }
    const paid = amountError('已付金额', v.paidAmount ?? '');
    if (paid) ctx.addIssue({ code: 'custom', path: ['paidAmount'], message: paid });
    // 有实收就必须说清走的什么渠道。这条闸门原先只在两端移动表单上有（#162），
    // web 空着付款方式也能提交——#152 那 20 张「已付>0 而方式为空」的单就是这么落库的。
    // 判据用 `paid > 0` 而不是付款状态：状态为「未付款」等价于实收为零，与订单金额无关，
    // 而这里拿不到明细合计（schema 看不见 orderItems）。
    if (Number(v.paidAmount || 0) > 0 && !(v.paymentMethod ?? '').trim()) {
      ctx.addIssue({ code: 'custom', path: ['paymentMethod'], message: '请选择付款方式' });
    }
  });
}

// 默认导出：定义为严格模式（不允许过去日期），保持向后兼容性
export const orderFormSchema = createOrderFormSchema(false);

export type OrderFormData = z.infer<typeof orderFormSchema>;
